/**
 * Platform maintenance window — the pure core shared by both apps.
 *
 * The runtime (src/maintenance.ts) and the web app (lib/maintenance.ts) each
 * keep their own pooled TTL-cached reader — that's I/O and stays out of this
 * package — but the phase semantics and the 503 wire contract live here once,
 * because two different answers to "is maintenance active right now?" means
 * the web holds pages while the runtime serves, or serves while the runtime
 * 503s. Admission per README: pure date math, runtime-agnostic,
 * dangerous-if-drifted.
 *
 * Semantics: start-INCLUSIVE, end-EXCLUSIVE — `UPDATE ends_at = now()` lifts
 * the window immediately, which the ops runbook relies on.
 */
export interface MaintenanceWindow {
  startsAt: Date | null;
  endsAt: Date | null;
  message: string | null;
}

export type MaintenancePhase = "none" | "upcoming" | "active";

export function evaluateMaintenance(win: MaintenanceWindow, now: Date = new Date()): MaintenancePhase {
  if (!win.startsAt || !win.endsAt) return "none";
  if (now.getTime() < win.startsAt.getTime()) return "upcoming";
  if (now.getTime() < win.endsAt.getTime()) return "active";
  return "none";
}

/** The `error` value both gates emit and clients string-match on
 *  (e.g. the pre-auth chat widget's friendly hold message). */
export const MAINTENANCE_ERROR_CODE = "maintenance_mode";

/** The 503 body both the runtime gate and the web proxy guard send —
 *  built in one place so the shapes cannot drift apart. */
export function maintenanceBody(message: string | null, endsAt: Date | null): {
  error: typeof MAINTENANCE_ERROR_CODE;
  message: string | null;
  ends_at: string | null;
} {
  return {
    error: MAINTENANCE_ERROR_CODE,
    message,
    ends_at: endsAt?.toISOString() ?? null,
  };
}

/** What `GET /api/maintenance` answers (the web app's lib/maintenance.ts
 *  builds it): the phase now, and the window's times as ISO strings. */
export interface MaintenanceState {
  phase: MaintenancePhase;
  /** ISO timestamps — serializable straight into client components. */
  startsAt: string | null;
  endsAt: string | null;
  message: string | null;
}

const isPhase = (v: unknown): v is MaintenancePhase => v === "none" || v === "upcoming" || v === "active";
const isoOrNull = (v: unknown): string | null => (typeof v === "string" && !Number.isNaN(Date.parse(v)) ? v : null);

/** A `GET /api/maintenance` answer read by a client that does not share the
 *  web app's types (the browser companion). Null for anything else: the
 *  caller keeps what it knew. A window needs both times to mean anything,
 *  so a phase without them reads as none. */
export function parseMaintenanceState(body: unknown): MaintenanceState | null {
  if (typeof body !== "object" || body === null || !("phase" in body) || !isPhase(body.phase)) return null;
  const startsAt = "startsAt" in body ? isoOrNull(body.startsAt) : null;
  const endsAt = "endsAt" in body ? isoOrNull(body.endsAt) : null;
  const message = "message" in body && typeof body.message === "string" ? body.message : null;
  const phase = startsAt === null || endsAt === null ? "none" : body.phase;
  return { phase, startsAt, endsAt, message };
}
